import React from 'react';

import { connect } from 'react-redux';

import { InputMoney } from '@nerdwallet/currency';
import num from '@nerdwallet/nw-client-lib/num';
import { NumStepper } from '@nerdwallet/react-stepper';

import { HOME_AFFORDABILITY } from '../../../lib/analytics-constants';
import { validationsSelector } from '../../../lib/selectors';
import {
  manualInputsSelector,
  inputsSelector,
} from '../../../lib/selectors/home-affordability-calculator-selectors';
import {
  HomeAffordabilityInput,
  INPUT_TRACKING_TYPES,
  inputOnChange,
} from '../../../lib/utils';
import { householdIncomeValidations } from '../validations';
import { debouncedTrackInputEvent } from '../../../lib/analytics/trackers';
import useAnalytics from '../../../lib/analytics/use-analytics';

type OnChangeEvent = React.ChangeEvent<HTMLInputElement>;

const HouseholdIncomeInput: React.FC<HomeAffordabilityInput> = ({
  manualInputs,
  inputs,
  dispatch,
  analytics,
  validations,
  label = '',
  inputType = 'TEXT',
}) => {
  const segmentAbstract = useAnalytics();
  // if inputType is RANGE we need to set a default value, so taking placeholder if needed
  const placeholder = num.formatCurrency(inputs?.income);

  const value =
    inputType === INPUT_TRACKING_TYPES.TEXT
      ? manualInputs?.income ?? ''
      : inputs?.income ?? placeholder ?? '';

  const errorMessage = validations?.income?.[0];
  const error = Boolean(errorMessage);
  const analyticsEnabled = analytics && analytics?.enabled;
  const legacyAnalytics =
    analyticsEnabled && analytics?.payload?.legacy?.formName;
  const bedrockAnalytics =
    analyticsEnabled && analytics?.payload?.bedrock?.eventType;

  const onInputChange = (e: OnChangeEvent) => {
    let legacy;
    if (legacyAnalytics) {
      const legacyProps = analytics?.payload?.legacy;
      legacy = {
        ...legacyProps,
        fieldName: 'income',
        inputType,
      };
    }

    let bedrock;
    if (bedrockAnalytics) {
      const bedrockProps = analytics?.payload?.bedrock;
      bedrock = {
        ...bedrockProps,
        valueFieldName: 'value_i',
      };
    }

    // segment analytics
    if (inputType === INPUT_TRACKING_TYPES.RANGE) {
      debouncedTrackInputEvent(
        {
          entityName: 'pre-tax_household_annual_income',
          field_type: 'increment-decrement-user_type_input',
          field: 'Annual household Income',
          field_value: e,
        },
        segmentAbstract
      );
    } else {
      debouncedTrackInputEvent(
        {
          entityName: 'pre-tax_household_annual_income',
          field_type: 'user_type_input',
          field: 'Annual household Income',
          field_value: e.target.value,
        },
        segmentAbstract
      );
    }

    inputOnChange({
      event: e,
      dispatch,
      legacy,
      bedrock,
      fieldName: 'income',
      calculator: HOME_AFFORDABILITY,
      inputType: 'currency',
    });
  };

  if (inputType === INPUT_TRACKING_TYPES.RANGE) {
    return (
      <NumStepper
        editable
        formatter="$0,0"
        max={householdIncomeValidations.max}
        min={householdIncomeValidations.min}
        stepSize={householdIncomeValidations.stepSize}
        value={value}
        onChange={onInputChange}
        subType="number"
      />
    );
  }

  return (
    <InputMoney
      label={label}
      value={value}
      placeholder={placeholder}
      error={error}
      errorMessage={errorMessage}
      onChange={onInputChange}
    />
  );
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  inputs: inputsSelector(state),
  validations: validationsSelector(state),
});

export default connect(mapStateToProps)(HouseholdIncomeInput);
